import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
	Alert,
	Box,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Link,
	MenuItem,
	Pagination,
	Paper,
	Select,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import routes from 'constants/routes';
import { countriesWithAgencyConfig } from 'features/auth/constants';
import { getInvoices } from '../../services';
import { IInvoiceListParams, IInvoiceSummary, InvoiceStatusEnum, InvoiceTypeEnum } from '../../types';
import {
	BU_LABEL,
	STATUS_COLOR,
	STATUS_LABEL,
	TYPE_COLOR,
	TYPE_LABEL,
	formatAmount,
	formatDate,
	isInvoiceOverdue,
	todayIso,
} from '../../uiHelpers';
import { extractErrorMessage } from '../../../quotation/helpers';

const PAGE_SIZE_OPTIONS = [20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_SORT = '-issueDate';

const ALL_TYPES = [InvoiceTypeEnum.Cash, InvoiceTypeEnum.Credit, InvoiceTypeEnum.CreditNote];
const ALL_STATUS = [InvoiceStatusEnum.Draft, InvoiceStatusEnum.Issued, InvoiceStatusEnum.Credited];

const SORT_OPTIONS: { value: string; label: string }[] = [
	{ value: '-issueDate', label: 'Emisión (recientes primero)' },
	{ value: 'issueDate', label: 'Emisión (antiguas primero)' },
	{ value: 'dueDate', label: 'Vencimiento (próximas primero)' },
	{ value: '-total', label: 'Mayor importe' },
];

const toLocalIso = (d: Date): string =>
	`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const addDaysIso = (days: number): string => {
	const d = new Date();
	d.setDate(d.getDate() + days);
	return toLocalIso(d);
};

const firstDayOfMonthIso = (): string => {
	const d = new Date();
	return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

export const InvoicesManagement = (): JSX.Element => {
	const history = useHistory();
	const [invoices, setInvoices] = useState<IInvoiceSummary[]>([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [types, setTypes] = useState<InvoiceTypeEnum[]>([]);
	const [statuses, setStatuses] = useState<InvoiceStatusEnum[]>([]);
	const [hasPendingBalance, setHasPendingBalance] = useState(false);
	const [isOverdue, setIsOverdue] = useState(false);
	const [saleOrderFullyCharged, setSaleOrderFullyCharged] = useState(false);
	const [issueDateFrom, setIssueDateFrom] = useState('');
	const [issueDateTo, setIssueDateTo] = useState('');
	const [dueDateFrom, setDueDateFrom] = useState('');
	const [dueDateTo, setDueDateTo] = useState('');
	const [search, setSearch] = useState('');
	const [passengerDocument, setPassengerDocument] = useState('');
	const [agencyId, setAgencyId] = useState<number | ''>('');
	const [sort, setSort] = useState<string>(DEFAULT_SORT);

	const params: IInvoiceListParams = useMemo(
		() => ({
			page,
			size,
			type: types.length ? types : undefined,
			status: statuses.length ? statuses : undefined,
			hasPendingBalance: hasPendingBalance || undefined,
			isOverdue: isOverdue || undefined,
			saleOrderFullyCharged: saleOrderFullyCharged || undefined,
			issueDateFrom: issueDateFrom || undefined,
			issueDateTo: issueDateTo || undefined,
			dueDateFrom: dueDateFrom || undefined,
			dueDateTo: dueDateTo || undefined,
			search: search.trim() || undefined,
			passengerDocument: passengerDocument.trim() || undefined,
			agencyId: agencyId === '' ? undefined : agencyId,
			sort,
		}),
		[
			page,
			size,
			types,
			statuses,
			hasPendingBalance,
			isOverdue,
			saleOrderFullyCharged,
			issueDateFrom,
			issueDateTo,
			dueDateFrom,
			dueDateTo,
			search,
			passengerDocument,
			agencyId,
			sort,
		],
	);

	const fetchInvoices = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const response = await getInvoices(params);
			setInvoices(response.elements);
			setTotal(response.total);
		} catch (err) {
			setError(extractErrorMessage(err, 'No se pudieron cargar las facturas.'));
		} finally {
			setLoading(false);
		}
	}, [params]);

	useEffect(() => {
		fetchInvoices();
	}, [fetchInvoices]);

	const toggleType = (type: InvoiceTypeEnum): void => {
		setPage(0);
		setTypes((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
	};

	const toggleStatus = (status: InvoiceStatusEnum): void => {
		setPage(0);
		setStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
	};

	/** Limpia todos los filtros (mantiene la BU, que es contexto de trabajo). */
	const clearFilters = (): void => {
		setPage(0);
		setTypes([]);
		setStatuses([]);
		setHasPendingBalance(false);
		setIsOverdue(false);
		setSaleOrderFullyCharged(false);
		setIssueDateFrom('');
		setIssueDateTo('');
		setDueDateFrom('');
		setDueDateTo('');
		setSearch('');
		setPassengerDocument('');
		setSort(DEFAULT_SORT);
	};

	/** Accesos rápidos: combos de filtros para los flujos comunes de cobranza. */
	const applyPreset = (preset: 'overdue' | 'dueSoon' | 'drafts' | 'issuedThisMonth' | 'weeklyReview'): void => {
		clearFilters();

		switch (preset) {
			case 'overdue':
				// Cobranza: emitidas, con saldo y ya vencidas, las más viejas primero
				setStatuses([InvoiceStatusEnum.Issued]);
				setHasPendingBalance(true);
				setIsOverdue(true);
				setSort('dueDate');
				break;
			case 'dueSoon':
				// Por vencer en los próximos 30 días, con saldo
				setStatuses([InvoiceStatusEnum.Issued]);
				setHasPendingBalance(true);
				setDueDateFrom(todayIso());
				setDueDateTo(addDaysIso(30));
				setSort('dueDate');
				break;
			case 'drafts':
				// Borradores pendientes de emitir
				setStatuses([InvoiceStatusEnum.Draft]);
				break;
			case 'issuedThisMonth':
				setStatuses([InvoiceStatusEnum.Issued]);
				setIssueDateFrom(firstDayOfMonthIso());
				setIssueDateTo(todayIso());
				break;
			case 'weeklyReview':
				// Revisión semanal de Nona: facturas con saldo en órdenes 100% cobradas
				// (candidatas a asociar recibos que el cron no matcheó exacto)
				setStatuses([InvoiceStatusEnum.Issued]);
				setHasPendingBalance(true);
				setSaleOrderFullyCharged(true);
				setSort('dueDate');
				break;
		}
	};

	const pendingCell = (invoice: IInvoiceSummary): JSX.Element => {
		if (invoice.status === InvoiceStatusEnum.Draft || invoice.type === InvoiceTypeEnum.CreditNote) {
			return (
				<Typography variant="body2" color="text.secondary">
					—
				</Typography>
			);
		}
		if (invoice.pendingAmount <= 0) {
			return <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />;
		}
		const overdue = isInvoiceOverdue(invoice);
		return (
			<Typography variant="body2" sx={{ color: overdue ? 'error.main' : 'warning.main', fontWeight: 600 }}>
				{formatAmount(invoice.pendingAmount, invoice.currency)}
			</Typography>
		);
	};

	const totalPages = Math.max(1, Math.ceil(total / size));

	return (
		<Paper sx={{ p: 2 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 2 }}>
				<Typography variant="h6">
					Facturas a clientes
					<Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
						{loading ? '' : `${total} resultado${total === 1 ? '' : 's'}`}
					</Typography>
				</Typography>
				<Link component="button" variant="body2" onClick={clearFilters} sx={{ cursor: 'pointer' }}>
					Limpiar filtros
				</Link>
			</Stack>

			{/* Accesos rápidos: combos de filtros para los flujos comunes */}
			<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" sx={{ mb: 1.5 }}>
				<Typography variant="caption" color="text.secondary">
					Accesos rápidos:
				</Typography>
				<Chip label="🔥 Vencidas con saldo" size="small" variant="outlined" onClick={() => applyPreset('overdue')} />
				<Chip label="⏳ Por vencer (30 días)" size="small" variant="outlined" onClick={() => applyPreset('dueSoon')} />
				<Chip label="📝 Borradores sin emitir" size="small" variant="outlined" onClick={() => applyPreset('drafts')} />
				<Chip
					label="🗓 Emitidas este mes"
					size="small"
					variant="outlined"
					onClick={() => applyPreset('issuedThisMonth')}
				/>
				<Tooltip title="Facturas con saldo cuya orden está 100% cobrada: candidatas a asociarles recibos que el cron no matcheó exacto">
					<Chip
						label="🔍 Revisión semanal"
						size="small"
						variant="outlined"
						onClick={() => applyPreset('weeklyReview')}
					/>
				</Tooltip>
			</Stack>

			<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
				{ALL_TYPES.map((type) => (
					<Chip
						key={type}
						label={TYPE_LABEL[type]}
						color={types.includes(type) ? TYPE_COLOR[type] : 'default'}
						variant={types.includes(type) ? 'filled' : 'outlined'}
						onClick={() => toggleType(type)}
						size="small"
					/>
				))}
				<Box sx={{ width: 8 }} />
				{ALL_STATUS.map((status) => (
					<Chip
						key={status}
						label={STATUS_LABEL[status]}
						color={statuses.includes(status) ? STATUS_COLOR[status] : 'default'}
						variant={statuses.includes(status) ? 'filled' : 'outlined'}
						onClick={() => toggleStatus(status)}
						size="small"
					/>
				))}
				<Box sx={{ width: 8 }} />
				<Chip
					label="Con saldo"
					color={hasPendingBalance ? 'warning' : 'default'}
					variant={hasPendingBalance ? 'filled' : 'outlined'}
					onClick={() => {
						setPage(0);
						setHasPendingBalance((v) => !v);
					}}
					size="small"
				/>
				<Chip
					label="Vencidas"
					color={isOverdue ? 'error' : 'default'}
					variant={isOverdue ? 'filled' : 'outlined'}
					onClick={() => {
						setPage(0);
						setIsOverdue((v) => !v);
					}}
					size="small"
				/>
				<Chip
					label="Orden 100% cobrada"
					color={saleOrderFullyCharged ? 'info' : 'default'}
					variant={saleOrderFullyCharged ? 'filled' : 'outlined'}
					onClick={() => {
						setPage(0);
						setSaleOrderFullyCharged((v) => !v);
					}}
					size="small"
				/>
			</Stack>

			<Grid container spacing={2} sx={{ mb: 2 }}>
				<Grid item xs={12} sm={6} md={3}>
					<TextField
						fullWidth
						size="small"
						label="Buscar"
						placeholder="Nro., pasajero, ref. externa"
						value={search}
						onChange={(e) => {
							setPage(0);
							setSearch(e.target.value);
						}}
					/>
				</Grid>
				<Grid item xs={12} sm={6} md={2}>
					<TextField
						fullWidth
						size="small"
						label="Doc. pasajero"
						placeholder="Documento exacto"
						value={passengerDocument}
						onChange={(e) => {
							setPage(0);
							setPassengerDocument(e.target.value);
						}}
					/>
				</Grid>
				<Grid item xs={6} sm={3} md={2}>
					<TextField
						fullWidth
						select
						size="small"
						label="BU"
						value={agencyId === '' ? '' : String(agencyId)}
						onChange={(e) => {
							setPage(0);
							setAgencyId(e.target.value === '' ? '' : Number(e.target.value));
						}}
					>
						<MenuItem value="">
							<em>Todas</em>
						</MenuItem>
						{countriesWithAgencyConfig.map((country) => (
							<MenuItem key={country.agencyId} value={String(country.agencyId)}>
								{country.countryName}
							</MenuItem>
						))}
					</TextField>
				</Grid>
				<Grid item xs={6} sm={3} md={3}>
					<TextField
						fullWidth
						select
						size="small"
						label="Ordenar por"
						value={sort}
						onChange={(e) => {
							setPage(0);
							setSort(e.target.value);
						}}
					>
						{SORT_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				</Grid>
				<Grid item xs={6} sm={3} md={2}>
					<TextField
						fullWidth
						size="small"
						type="date"
						label="Emisión desde"
						InputLabelProps={{ shrink: true }}
						value={issueDateFrom}
						onChange={(e) => {
							setPage(0);
							setIssueDateFrom(e.target.value);
						}}
					/>
				</Grid>
				<Grid item xs={6} sm={3} md={2}>
					<TextField
						fullWidth
						size="small"
						type="date"
						label="Emisión hasta"
						InputLabelProps={{ shrink: true }}
						value={issueDateTo}
						onChange={(e) => {
							setPage(0);
							setIssueDateTo(e.target.value);
						}}
					/>
				</Grid>
				<Grid item xs={6} sm={3} md={2}>
					<TextField
						fullWidth
						size="small"
						type="date"
						label="Vto. desde"
						InputLabelProps={{ shrink: true }}
						value={dueDateFrom}
						onChange={(e) => {
							setPage(0);
							setDueDateFrom(e.target.value);
						}}
					/>
				</Grid>
				<Grid item xs={6} sm={3} md={2}>
					<TextField
						fullWidth
						size="small"
						type="date"
						label="Vto. hasta"
						InputLabelProps={{ shrink: true }}
						value={dueDateTo}
						onChange={(e) => {
							setPage(0);
							setDueDateTo(e.target.value);
						}}
					/>
				</Grid>
			</Grid>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Nro. Factura</TableCell>
							<TableCell>Tipo</TableCell>
							<TableCell>Estado</TableCell>
							<TableCell>Pasajero / Empresa</TableCell>
							<TableCell>Orden</TableCell>
							<TableCell>Emisión</TableCell>
							<TableCell>Vencimiento</TableCell>
							<TableCell align="right">Total</TableCell>
							<TableCell align="right">USD</TableCell>
							<TableCell align="right">Saldo</TableCell>
							<TableCell>BU</TableCell>
							<TableCell>Ref. externa</TableCell>
							<TableCell align="center">Acciones</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={13} align="center" sx={{ py: 4 }}>
									<CircularProgress size={28} />
								</TableCell>
							</TableRow>
						) : invoices.length === 0 ? (
							<TableRow>
								<TableCell colSpan={13} align="center" sx={{ py: 4 }}>
									<Typography variant="body2" color="text.secondary">
										No hay facturas para los filtros seleccionados.
									</Typography>
								</TableCell>
							</TableRow>
						) : (
							invoices.map((invoice) => {
								const overdue = isInvoiceOverdue(invoice);
								return (
									<TableRow key={invoice.id} hover>
										<TableCell>
											<Typography
												variant="body2"
												sx={{
													fontFamily: 'monospace',
													color: invoice.status === InvoiceStatusEnum.Draft ? 'text.secondary' : 'text.primary',
												}}
											>
												{invoice.invoiceNumber}
											</Typography>
										</TableCell>
										<TableCell>
											<Chip label={TYPE_LABEL[invoice.type]} color={TYPE_COLOR[invoice.type]} size="small" />
										</TableCell>
										<TableCell>
											<Chip label={STATUS_LABEL[invoice.status]} color={STATUS_COLOR[invoice.status]} size="small" />
										</TableCell>
										<TableCell>
											<Stack direction="row" spacing={0.5} alignItems="center">
												{invoice.passengerIsPhysical ? (
													<PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
												) : (
													<BusinessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
												)}
												<Typography variant="body2">{invoice.passengerName}</Typography>
											</Stack>
										</TableCell>
										<TableCell>
											<Tooltip title="Abrir detalle de la orden" arrow>
												<Link
													component="button"
													variant="body2"
													onClick={() => history.push(routes.salesOrder.edit.replace(':id', invoice.saleOrderId))}
													sx={{ cursor: 'pointer' }}
												>
													{invoice.isBitrixDeal ? `Deal: ${invoice.dealId}` : `Caso: ${invoice.dealId}`}
												</Link>
											</Tooltip>
										</TableCell>
										<TableCell>{formatDate(invoice.issueDate)}</TableCell>
										<TableCell>
											<Typography variant="body2" sx={{ color: overdue ? 'error.main' : 'text.primary' }}>
												{formatDate(invoice.dueDate)}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Typography variant="body2" sx={{ color: invoice.total < 0 ? 'error.main' : 'text.primary' }}>
												{formatAmount(invoice.total, invoice.currency)}
											</Typography>
										</TableCell>
										<TableCell align="right">
											{invoice.currency !== 'USD' ? formatAmount(invoice.totalInMainCurrency, 'USD') : '—'}
										</TableCell>
										<TableCell align="right">{pendingCell(invoice)}</TableCell>
										<TableCell>{BU_LABEL[invoice.agencyId] ?? invoice.agencyId}</TableCell>
										<TableCell>
											<Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
												{invoice.externalReference ?? '—'}
											</Typography>
										</TableCell>
										<TableCell align="center">
											{invoice.pdfUrl && (
												<Tooltip title="Ver PDF">
													<IconButton size="small" onClick={() => window.open(invoice.pdfUrl, '_blank')}>
														<PictureAsPdfIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</Tooltip>
											)}
										</TableCell>
									</TableRow>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>

			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
				<Stack direction="row" spacing={1} alignItems="center">
					<Typography variant="body2" color="text.secondary">
						{total} facturas
					</Typography>
					<Select
						size="small"
						value={size}
						onChange={(e) => {
							setPage(0);
							setSize(Number(e.target.value));
						}}
					>
						{PAGE_SIZE_OPTIONS.map((opt) => (
							<MenuItem key={opt} value={opt}>
								{opt} / pág.
							</MenuItem>
						))}
					</Select>
				</Stack>
				<Pagination
					count={totalPages}
					page={page + 1}
					onChange={(_, value) => setPage(value - 1)}
					color="primary"
					size="small"
				/>
			</Box>
		</Paper>
	);
};
